import { useEffect, useState, useCallback } from "react";
import { Loader2, TrendingUp, TrendingDown } from "lucide-react";
import { AreaChart, Area, BarChart, Bar, XAxis, Tooltip, ResponsiveContainer } from "recharts";
import { API } from "../lib/api";

const RANGES = [
  { k: "7d", l: "7 giorni" }, { k: "30d", l: "30 giorni" }, { k: "90d", l: "90 giorni" }, { k: "ytd", l: "Anno" },
];

const eur = (v) => "€" + Number(v || 0).toLocaleString("it-IT", { maximumFractionDigits: 0 });

export default function AdminDashboard() {
  const [range, setRange] = useState("30d");
  const [custom, setCustom] = useState({ start: "", end: "" });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    let url = `${API}/admin/dashboard?range=${range}`;
    if (range === "custom" && custom.start && custom.end) url += `&start=${custom.start}&end=${custom.end}`;
    try { const r = await fetch(url, { credentials: "include" }); setData(await r.json()); } catch { /* */ }
    setLoading(false);
  }, [range, custom]);

  useEffect(() => { load(); }, [load]);

  const k = data?.kpis || {};
  const KPI = [
    { key: "revenue", label: "Ricavi", fmt: eur }, { key: "orders", label: "Ordini" },
    { key: "aov", label: "Valore medio", fmt: eur }, { key: "visitors", label: "Visitatori" },
    { key: "page_views", label: "Pagine viste" }, { key: "conversion_rate", label: "Conversion", suffix: "%" },
    { key: "executive_calls", label: "Executive Call" }, { key: "newsletter", label: "Newsletter" },
    { key: "memberships", label: "Membership" }, { key: "contacts", label: "Contatti" },
  ];

  return (
    <div data-testid="admin-dashboard-home">
      {/* range filter */}
      <div className="flex flex-wrap items-center gap-2 mb-8">
        {RANGES.map((r) => (
          <button key={r.k} data-testid={`range-${r.k}`} onClick={() => setRange(r.k)}
            className={`px-4 py-2 text-xs uppercase tracking-[0.12em] border transition-[background-color,color] ${range === r.k ? "bg-[var(--petrol)] text-white border-[var(--petrol)]" : "border-[var(--line)] text-[var(--ink-soft)] hover:border-[var(--petrol)]"}`}>{r.l}</button>
        ))}
        <div className="flex items-center gap-2 ml-auto">
          <input type="date" value={custom.start} onChange={(e) => setCustom({ ...custom, start: e.target.value })} className="border border-[var(--line)] px-2 py-1.5 text-xs" />
          <span className="text-[var(--ink-soft)]">→</span>
          <input type="date" value={custom.end} onChange={(e) => setCustom({ ...custom, end: e.target.value })} className="border border-[var(--line)] px-2 py-1.5 text-xs" />
          <button data-testid="range-custom" onClick={() => setRange("custom")} className="px-3 py-1.5 text-xs uppercase tracking-[0.12em] border border-[var(--gold)] text-[var(--accent)]">Applica</button>
        </div>
      </div>

      {loading || !data ? <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-[var(--petrol)]" /></div> : (
        <>
          {/* KPI grid */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-px bg-[var(--line)] border border-[var(--line)] mb-8">
            {KPI.map((kp) => {
              const item = k[kp.key] || { value: 0, delta: 0 };
              const up = item.delta >= 0;
              const val = kp.fmt ? kp.fmt(item.value) : item.value + (kp.suffix || "");
              return (
                <div key={kp.key} data-testid={`kpi-${kp.key}`} className="bg-white p-5">
                  <div className="overline !text-[var(--anthracite)] mb-2">{kp.label}</div>
                  <div className="font-serif text-3xl text-[var(--ink)]">{val}</div>
                  <div className={`mt-1 inline-flex items-center gap-1 text-xs ${up ? "text-emerald-600" : "text-red-500"}`}>
                    {up ? <TrendingUp size={13} /> : <TrendingDown size={13} />} {up ? "+" : ""}{item.delta}% <span className="text-[var(--ink-soft)]">vs prec.</span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* charts */}
          <div className="grid lg:grid-cols-2 gap-6 mb-8">
            <ChartCard title="Ricavi">
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={data.timeseries}>
                  <defs><linearGradient id="g1" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#4a3f30" stopOpacity={0.3} /><stop offset="100%" stopColor="#4a3f30" stopOpacity={0} /></linearGradient></defs>
                  <XAxis dataKey="day" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                  <Tooltip formatter={(v) => eur(v)} />
                  <Area type="monotone" dataKey="revenue" stroke="#4a3f30" fill="url(#g1)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </ChartCard>
            <ChartCard title="Traffico (pagine viste)">
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={data.timeseries}>
                  <defs><linearGradient id="g2" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#b8965a" stopOpacity={0.4} /><stop offset="100%" stopColor="#b8965a" stopOpacity={0} /></linearGradient></defs>
                  <XAxis dataKey="day" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                  <Tooltip />
                  <Area type="monotone" dataKey="views" stroke="#b8965a" fill="url(#g2)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>

          {/* tops + funnels */}
          <div className="grid lg:grid-cols-3 gap-6 mb-8">
            <TopList title="Top prodotti" items={data.top_products} render={(p) => [p.name, `${p.orders} · ${eur(p.revenue)}`]} empty="Nessuna vendita nel periodo" />
            <TopList title="Top pagine" items={data.top_pages} render={(p) => [p.path, `${p.views} viste`]} empty="Nessun dato traffico" />
            <TopList title="Top canali" items={data.top_channels} render={(p) => [p.channel, `${p.visits} visite`]} empty="Nessun dato canali" />
          </div>

          <ChartCard title="Funnel — Digital Products">
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={data.funnels.products} layout="vertical" margin={{ left: 30 }}>
                <XAxis type="number" hide /><Tooltip />
                <Bar dataKey="n" fill="#4a3f30" radius={[0, 4, 4, 0]} />
                <XAxis dataKey="step" type="category" hide />
              </BarChart>
            </ResponsiveContainer>
            <div className="grid grid-cols-5 text-center text-xs mt-2">
              {data.funnels.products.map((f) => <div key={f.step}><div className="font-serif text-lg text-[var(--petrol)]">{f.n}</div><div className="text-[var(--ink-soft)]">{f.step}</div></div>)}
            </div>
          </ChartCard>

          {/* Academy */}
          <div className="mt-10 mb-8" data-testid="dashboard-academy">
            <div className="overline !text-[var(--anthracite)] mb-4">Academy</div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-[var(--line)] border border-[var(--line)] mb-6">
              <Mini testid="academy-applications" label="Candidature" v={data.academy?.applications} />
              <Mini testid="academy-conversion" label="Conversion candidatura" v={`${data.academy?.conversion_rate ?? 0}%`} />
              <Mini testid="academy-enrollments" label="Iscrizioni (pagamenti)" v={data.academy?.enrollments} />
              <Mini testid="academy-revenue" label="Ricavi Academy" v={eur(data.academy?.revenue)} />
              <Mini testid="academy-views" label="Visite pagine corso" v={data.academy?.page_views} />
              <Mini testid="academy-cta" label="Click CTA" v={data.academy?.cta_clicks} />
              <Mini testid="academy-approvals" label="Approvazioni" v={data.academy?.approvals} />
              <Mini testid="academy-fill" label="Fill rate posti" v={`${data.academy?.fill_rate ?? 0}%`}
                sub={data.academy?.seats_total ? `${data.academy.seats_taken}/${data.academy.seats_total} posti` : "Posti non configurati"} />
            </div>
            <div className="grid lg:grid-cols-2 gap-6">
              <Funnel title="Funnel — Academy" steps={data.funnels.academy} />
              <TopList title="Top programmi" items={data.academy?.top_programs}
                render={(p) => [p.program, `${p.applications} candidature · ${eur(p.revenue)}`]}
                empty="Nessuna candidatura nel periodo" />
            </div>
          </div>

          {/* Membership */}
          <div className="mb-8" data-testid="dashboard-membership">
            <div className="overline !text-[var(--anthracite)] mb-4">Executive Club — Membership</div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-[var(--line)] border border-[var(--line)]">
              <Mini testid="memb-active" label="Membri attivi" v={data.membership?.active} />
              <Mini testid="memb-new" label="Nuovi nel periodo" v={data.membership?.new} />
              <Mini testid="memb-mrr" label="MRR" v={eur(data.membership?.mrr)} />
              <Mini testid="memb-arr" label="ARR" v={eur(data.membership?.arr)} />
              <Mini testid="memb-arpu" label="ARPU mensile" v={eur(data.membership?.arpu)} />
              <Mini testid="memb-churn" label="Churn" v={`${data.membership?.churn ?? 0}%`} />
              <Mini testid="memb-monthly" label="Mensili (€39)" v={data.membership?.monthly} />
              <Mini testid="memb-yearly" label="Annuali (€390)" v={data.membership?.yearly} />
            </div>
          </div>

          <div className="mt-8 space-y-4">
            <Ga4Section range={range} custom={custom} />
            <GscSection range={range} custom={custom} />
          </div>
        </>
      )}
    </div>
  );
}

const ChartCard = ({ title, children }) => (
  <div className="bg-white border border-[var(--line)] p-5">
    <div className="overline !text-[var(--anthracite)] mb-4">{title}</div>
    {children}
  </div>
);
const TopList = ({ title, items, render, empty }) => (
  <div className="bg-white border border-[var(--line)] p-5">
    <div className="overline !text-[var(--anthracite)] mb-4">{title}</div>
    {(!items || !items.length) ? <p className="text-sm text-[var(--ink-soft)]">{empty}</p> : (
      <ul className="space-y-3">{items.map((it, i) => { const [a, b] = render(it); return (
        <li key={i} className="flex items-center justify-between gap-3 text-sm border-b border-[var(--line)] last:border-0 pb-2">
          <span className="text-[var(--ink)] truncate">{a}</span><span className="text-[var(--ink-soft)] shrink-0">{b}</span>
        </li>); })}</ul>
    )}
  </div>
);
const Mini = ({ label, v, sub, testid }) => (
  <div className="bg-white p-5" data-testid={testid}>
    <div className="overline !text-[var(--anthracite)] mb-2">{label}</div>
    <div className="font-serif text-2xl text-[var(--ink)]">{v ?? 0}</div>
    {sub && <div className="text-xs text-[var(--ink-soft)] mt-1">{sub}</div>}
  </div>
);
const Funnel = ({ title, steps }) => (
  <ChartCard title={title}>
    <div className="space-y-2">
      {(steps || []).map((s) => {
        const max = steps?.[0]?.n || 1;
        return (
          <div key={s.step} className="flex items-center gap-3">
            <span className="w-28 shrink-0 text-xs text-[var(--ink-soft)]">{s.step}</span>
            <div className="flex-1 h-6 bg-[var(--paper-2)]">
              <div className="h-6 bg-[var(--petrol)] transition-[width] duration-500" style={{ width: `${Math.max((s.n / max) * 100, s.n ? 4 : 0)}%` }} />
            </div>
            <span className="w-10 text-right font-serif text-[var(--petrol)]">{s.n}</span>
          </div>
        );
      })}
    </div>
  </ChartCard>
);
const Ga4Section = ({ range, custom }) => {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true); setErr(null);
    let url = `${API}/admin/ga4?range=${range}`;
    if (range === "custom" && custom.start && custom.end) url += `&start=${custom.start}&end=${custom.end}`;
    fetch(url, { credentials: "include" })
      .then(async (r) => {
        if (!r.ok) { const x = await r.json().catch(() => ({})); throw new Error(x.detail || "Errore di connessione"); }
        return r.json();
      })
      .then(setD).catch((e) => setErr(e.message)).finally(() => setLoading(false));
  }, [range, custom]);

  const empty = d && !d.totals.sessions && !d.totals.users && !d.totals.views;

  return (
    <div className="bg-white border border-[var(--line)] p-5" data-testid="ga4-section">
      <div className="flex items-center justify-between mb-4">
        <div className="overline !text-[var(--anthracite)]">Google Analytics 4</div>
        <span className="text-[0.65rem] uppercase tracking-[0.12em] px-2 py-1 border border-emerald-600/40 text-emerald-700" data-testid="ga4-status">
          Collegato · G-G90F2W9B4B
        </span>
      </div>
      {loading && <div className="py-8 flex justify-center"><Loader2 className="animate-spin text-[var(--petrol)]" /></div>}
      {!loading && err && <p className="text-sm text-red-700" data-testid="ga4-error">{err}</p>}
      {!loading && d && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-6 gap-px bg-[var(--line)] border border-[var(--line)] mb-6">
            <Mini testid="ga4-users" label="Utenti" v={d.totals.users} />
            <Mini testid="ga4-sessions" label="Sessioni" v={d.totals.sessions} />
            <Mini testid="ga4-views" label="Visualizzazioni" v={d.totals.views} />
            <Mini testid="ga4-new" label="Nuovi utenti" v={d.totals.new_users} />
            <Mini testid="ga4-engagement" label="Engagement" v={`${d.totals.engagement}%`} />
            <Mini testid="ga4-conversions" label="Conversioni" v={d.totals.conversions} />
          </div>
          {empty && (
            <p className="text-sm text-[var(--ink-soft)] mb-4" data-testid="ga4-warming">
              Tag installato da poco: i dati GA4 compaiono entro 24-48 ore dalla prima visita misurata.
            </p>
          )}
          {d.daily.length > 0 && (
            <div className="mb-6">
              <div className="text-xs uppercase tracking-[0.12em] text-[var(--ink-soft)] mb-2">Andamento sessioni / utenti</div>
              <ResponsiveContainer width="100%" height={180}>
                <AreaChart data={d.daily}>
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} /><Tooltip />
                  <Area type="monotone" dataKey="sessions" stroke="#4a3f30" fill="#4a3f3022" name="Sessioni" />
                  <Area type="monotone" dataKey="users" stroke="#b8965a" fill="#b8965a22" name="Utenti" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
          <div className="grid sm:grid-cols-3 gap-6 text-sm">
            <div data-testid="ga4-pages">
              <div className="text-xs uppercase tracking-[0.12em] text-[var(--ink-soft)] mb-2">Pagine più viste</div>
              {d.top_pages.length === 0 && <span className="text-[var(--ink-soft)]">—</span>}
              {d.top_pages.map((p) => (
                <div key={p.pagePath} className="flex justify-between py-1 border-b border-[var(--line)]">
                  <span className="truncate mr-3">{p.pagePath}</span>
                  <span className="font-serif text-[var(--petrol)]">{p.screenPageViews}</span>
                </div>
              ))}
            </div>
            <div data-testid="ga4-countries">
              <div className="text-xs uppercase tracking-[0.12em] text-[var(--ink-soft)] mb-2">Geografia</div>
              {d.countries.length === 0 && <span className="text-[var(--ink-soft)]">—</span>}
              {d.countries.map((c) => (
                <div key={c.country} className="flex justify-between py-1 border-b border-[var(--line)]">
                  <span>{c.country}</span><span className="font-serif text-[var(--petrol)]">{c.activeUsers}</span>
                </div>
              ))}
            </div>
            <div data-testid="ga4-devices">
              <div className="text-xs uppercase tracking-[0.12em] text-[var(--ink-soft)] mb-2">Device</div>
              {d.devices.length === 0 && <span className="text-[var(--ink-soft)]">—</span>}
              {d.devices.map((dv) => (
                <div key={dv.deviceCategory} className="flex justify-between py-1 border-b border-[var(--line)]">
                  <span>{dv.deviceCategory}</span><span className="font-serif text-[var(--petrol)]">{dv.sessions}</span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const GscSection = ({ range, custom }) => {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true); setErr(null);
    let url = `${API}/admin/gsc?range=${range}`;
    if (range === "custom" && custom.start && custom.end) url += `&start=${custom.start}&end=${custom.end}`;
    fetch(url, { credentials: "include" })
      .then(async (r) => {
        if (!r.ok) { const x = await r.json().catch(() => ({})); throw new Error(x.detail || "Errore di connessione"); }
        return r.json();
      })
      .then(setD).catch((e) => setErr(e.message)).finally(() => setLoading(false));
  }, [range, custom]);

  const empty = d && !d.totals.impressions && !d.totals.clicks;

  return (
    <div className="bg-white border border-[var(--line)] p-5" data-testid="gsc-section">
      <div className="flex items-center justify-between mb-4">
        <div className="overline !text-[var(--anthracite)]">SEO — Google Search Console</div>
        <span className="text-[0.65rem] uppercase tracking-[0.12em] px-2 py-1 border border-emerald-600/40 text-emerald-700" data-testid="gsc-status">
          Collegato · mariagabriellaritondo.com
        </span>
      </div>
      {loading && <div className="py-8 flex justify-center"><Loader2 className="animate-spin text-[var(--petrol)]" /></div>}
      {!loading && err && <p className="text-sm text-red-700" data-testid="gsc-error">{err}</p>}
      {!loading && d && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-[var(--line)] border border-[var(--line)] mb-6">
            <Mini testid="gsc-clicks" label="Click totali" v={d.totals.clicks} />
            <Mini testid="gsc-impressions" label="Impressioni" v={d.totals.impressions} />
            <Mini testid="gsc-ctr" label="CTR medio" v={`${d.totals.ctr}%`} />
            <Mini testid="gsc-position" label="Posizione media" v={d.totals.position || "—"} />
          </div>
          {empty && (
            <p className="text-sm text-[var(--ink-soft)] mb-4" data-testid="gsc-warming">
              Dominio appena collegato e sito in noindex: Google inizierà a mostrare dati dopo il go-live pubblico (di solito entro 1-2 settimane dall'indicizzazione).
            </p>
          )}
          {d.daily.length > 0 && (
            <div className="mb-6">
              <div className="text-xs uppercase tracking-[0.12em] text-[var(--ink-soft)] mb-2">Andamento impressioni / click</div>
              <ResponsiveContainer width="100%" height={180}>
                <AreaChart data={d.daily}>
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} /><Tooltip />
                  <Area type="monotone" dataKey="impressions" stroke="#b8965a" fill="#b8965a22" name="Impressioni" />
                  <Area type="monotone" dataKey="clicks" stroke="#4a3f30" fill="#4a3f3022" name="Click" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
          <div className="grid sm:grid-cols-2 gap-6 text-sm">
            <div data-testid="gsc-queries">
              <div className="text-xs uppercase tracking-[0.12em] text-[var(--ink-soft)] mb-2">Query di ricerca</div>
              {d.top_queries.length === 0 && <span className="text-[var(--ink-soft)]">—</span>}
              {d.top_queries.map((q) => (
                <div key={q.query} className="flex justify-between py-1 border-b border-[var(--line)]">
                  <span className="truncate mr-3">{q.query}</span>
                  <span className="font-serif text-[var(--petrol)] whitespace-nowrap">{q.clicks} click · {q.impressions} impr.</span>
                </div>
              ))}
            </div>
            <div data-testid="gsc-pages">
              <div className="text-xs uppercase tracking-[0.12em] text-[var(--ink-soft)] mb-2">Pagine per traffico SEO</div>
              {d.top_pages.length === 0 && <span className="text-[var(--ink-soft)]">—</span>}
              {d.top_pages.map((p) => (
                <div key={p.page} className="flex justify-between py-1 border-b border-[var(--line)]">
                  <span className="truncate mr-3">{p.page.replace("https://mariagabriellaritondo.com", "") || "/"}</span>
                  <span className="font-serif text-[var(--petrol)] whitespace-nowrap">{p.clicks} click · {p.impressions} impr.</span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};
